import React from "react";
import { Link } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";

export default function Contact() {
  return (
    <div className="bg-[#FAF9F6] text-stone-800 min-h-screen">
      <SEO
        title="Kontakt ET Danmark ApS | Ring 42 49 95 95 for Rådgivning & Tilbud"
        description="Kontakt ET Danmark for et uforpligtende tilbud på energimærkning, trykprøvning eller energirammeberegning i Jylland og Fyn. Ring 42 49 95 95 eller send en besked her."
        canonical="https://www.etdanmark.com/kontakt"
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07577.jpg"
      />
      {/* 1. TOP SLIDER HERO */}
      <PageTopSlider
        title="Kontakt os"
        subtitle="Vi bestræber os altid på at besvare alle henvendelser hurtigst muligt. Har du et akut spørgsmål eller brug for afklaring i dag, er du altid velkommen til at ringe direkte."
        badge="Hurtig tilbagemelding inden for 24 timer"
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Kontakt" },
        ]}
        actions={
          <>
            <a
              href="tel:+4542499595"
              className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
              <span>Ring: 42 49 95 95</span>
            </a>
            <a
              href="mailto:info@etdanmark.com"
              className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-envelope text-emerald-300 text-xs" aria-hidden="true"></i>
              <span>info@etdanmark.com</span>
            </a>
          </>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg",
          alt: "Kontakt ET Danmark ApS",
        }}
      />

      {/* Main Contact Section with details and form */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left: Contact Info Cards */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-[#FAF9F6] p-7 rounded-3xl border border-stone-200/80 space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-stone-900">ET Danmark ApS</h2>
                  <p className="text-xs text-stone-500 mt-0.5">CVR: 41026138</p>
                </div>

                {/* Telefon */}
                <div className="flex items-start gap-3.5">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-phone text-base" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-xs text-stone-500 block">Telefon:</span>
                    <a
                      href="tel:+4542499595"
                      className="text-lg font-bold text-stone-900 hover:text-emerald-700 whitespace-nowrap"
                    >
                      42 49 95 95
                    </a>
                    <span className="text-xs text-stone-500 block mt-0.5">
                      Ring for direkte rådgivning og tidsbestilling
                    </span>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-3.5">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-envelope text-base" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-xs text-stone-500 block">Email:</span>
                    <a
                      href="mailto:info@etdanmark.com"
                      className="text-base font-bold text-stone-900 hover:text-emerald-700 whitespace-nowrap"
                    >
                      info@etdanmark.com
                    </a>
                    <span className="text-xs text-stone-500 block mt-0.5">
                      Vi bestræber os på at besvare mails inden for 24 timer
                    </span>
                  </div>
                </div>

                {/* Adresser */}
                <div className="pt-4 border-t border-stone-200/80 space-y-4 text-xs text-stone-700">
                  <div className="flex items-start gap-3">
                    <i className="fa-solid fa-location-dot text-[#165B4C] mt-0.5 shrink-0" aria-hidden="true"></i>
                    <div>
                      <p className="font-bold text-stone-900 text-sm">Adresse</p>
                      <p className="mt-0.5 text-stone-600">Bjerrevej 62 A, 8700 Horsens</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Calculator CTA Box */}
              <div className="bg-emerald-900 text-white p-7 rounded-3xl space-y-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-emerald-300 block">
                  Vil du vide hvad det koster?
                </span>
                <h3 className="text-xl font-bold">Prøv vores online prisberegner</h3>
                <p className="text-xs sm:text-sm text-emerald-100 leading-relaxed">
                  Få et øjeblikkeligt estimat på energimærkning, trykprøvning eller termografi for din boligtype og størrelse.
                </p>
                <div className="pt-2">
                  <Link
                    to="/prisberegner"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white text-stone-900 font-bold text-xs hover:bg-stone-100 transition-colors shadow-xs"
                  >
                    <i className="fa-solid fa-calculator text-emerald-700" aria-hidden="true"></i>
                    <span>Åbn prisberegneren &rarr;</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm
                title="Kontakt os"
                subtitle="Du kan let og hurtigt sende os en besked i nedenstående formular."
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
